import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { ShipmentApiService } from '../../core/api/shipment/shipment-api.service';
import { resolveCancelErrorI18nKey } from '../../core/api/shipment/models/cancel-shipment.model';
import { formatShipFromLocalDate } from '../../core/api/shipment/models/completed-shipment.model';
import { ShipmentFlowStateService } from '../../core/services/shipment-flow-state.service';
import { TranslatePipe } from '../../core/i18n';
import { AlertToast } from '../../shared/components/alert-toast/alert-toast';
import { GetLabelsCancelShipmentDialog } from '../get-labels/sections/get-labels-cancel-shipment-dialog/get-labels-cancel-shipment-dialog';
import { GetLabelsCancelledShipmentDialog } from '../get-labels/sections/get-labels-cancelled-shipment-dialog/get-labels-cancelled-shipment-dialog';

/**
 * Email deep-link entry for OM-69 cancel (token from OM-70 confirmation email).
 * Query: `?token=...&flowId=...` (flowId binds the attestation).
 */
@Component({
  selector: 'app-cancel-shipment',
  imports: [
    AlertToast,
    ButtonComponent,
    GetLabelsCancelShipmentDialog,
    GetLabelsCancelledShipmentDialog,
    TranslatePipe,
    TypographyComponent,
  ],
  templateUrl: './cancel-shipment.html',
  styleUrl: './cancel-shipment.scss',
})
export class CancelShipmentPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly shipmentApi = inject(ShipmentApiService);
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);

  private cancelToken = '';
  private flowId = '';

  protected readonly dialogOpen = signal(false);
  protected readonly cancelledDialogOpen = signal(false);
  protected readonly submitting = signal(false);
  protected readonly cancelled = signal(false);
  protected readonly errorKey = signal<string | null>(null);
  protected readonly invalidLink = signal(false);
  protected readonly trackingPin = signal('');
  protected readonly shipmentCreationDate = signal('');
  /** True when cancel API soft-cancelled a linked pickup (OM-890 success copy). */
  protected readonly pickupCancelledOnCancel = signal(false);

  /**
   * Reads token/flowId from the query string and opens the cancel confirmation dialog.
   * Defers open so the navigation click does not dismiss via backdrop.
   */
  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.cancelToken = params.get('token')?.trim() ?? '';
    this.flowId = params.get('flowId')?.trim() ?? '';

    if (!this.cancelToken || !this.flowId) {
      this.invalidLink.set(true);
      return;
    }

    queueMicrotask(() => {
      this.dialogOpen.set(true);
    });
  }

  /**
   * Closes the dialog without cancelling and returns home.
   */
  protected onDismissed(): void {
    if (this.submitting()) {
      return;
    }
    this.dialogOpen.set(false);
    this.router.navigateByUrl('/').catch(() => undefined);
  }

  /**
   * Submits cancel using the email attestation token.
   * Session-less deep links without a persisted shipment still fail at the API (expected).
   */
  protected async onConfirmed(): Promise<void> {
    if (this.submitting() || !this.cancelToken || !this.flowId) {
      return;
    }

    this.submitting.set(true);
    this.errorKey.set(null);

    try {
      const completed = this.shipmentFlowState.load()?.completedShipment;
      const result = await this.shipmentApi.cancel(
        {
          localDate: formatShipFromLocalDate(completed),
          cancelToken: this.cancelToken,
        },
        { flowId: this.flowId },
      );
      this.trackingPin.set(result.trackingPin);
      this.shipmentCreationDate.set(result.shipmentCreationDate);
      this.pickupCancelledOnCancel.set(Boolean(result.pickupCancelled));
      this.cancelled.set(true);
      this.dialogOpen.set(false);
      queueMicrotask(() => {
        this.cancelledDialogOpen.set(true);
      });
    } catch (error) {
      this.errorKey.set(resolveCancelErrorI18nKey(error));
      this.dialogOpen.set(false);
    } finally {
      this.submitting.set(false);
    }
  }

  /**
   * Returns the customer to the home / create-shipment start.
   */
  protected onGoHome(): void {
    this.cancelledDialogOpen.set(false);
    this.router.navigateByUrl('/').catch(() => undefined);
  }
}
